import { useState, useCallback } from "react";
import {
  Col,
  Row,
  Card,
  CardTitle,
  Button,
  CardSubtitle,
  Form,
  Label,
} from "reactstrap";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DeleteClass,
  SaveProgramFocusControlSetting,
} from "../../actions/teacherMutations";
import { FormattedMessage, useIntl } from "react-intl";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import CreateClassInProgram from "../Modal/CreateClassInProgram";
import ConfirmDialog from "../Modal/ConfirmDialog";
import DataGrid from "../DataGrids/DataGrid";
import ImportStudentsToClassModal from "../Modal/ImportStudentsToClassModal";
import FocusControl from "../Teacher/FocusControl";
import useFocusControl from "./hooks/useFocusControl";
import { compareByName, compareStrings } from "../../Utilities/sortHelper";
import useChooseSidebarClassesStore from "../../stores/useChooseSidebarClassesStore";
import RequiresFullAccessHint from "./RequiresFullAccessHint";

export default function ProgramAdministration(props) {
  const [promptClassRemoval, setPromptClassRemoval] = useState(false);
  const [classForRemoval, setClassForRemoval] = useState(null);
  const [addClassModalOpen, setAddClassModalOpen] = useState(false);
  const [importStudentsModalOpen, setImportStudentsModalOpen] = useState(false);
  const [createdClassId, setCreatedClassId] = useState(null);
  const [
    importStudentsAfterCreatingClass,
    setImportStudentsAfterCreatingClass,
  ] = useState(false);

  const deleteClassMutation = useMutation({ mutationFn: DeleteClass });
  const queryClient = useQueryClient();
  const intl = useIntl();
  const openChooseSidebarClasses = useChooseSidebarClassesStore(
    (state) => state.open,
  );

  const buildDeviantData = useCallback((fcSettings) => {
    const moduleItem = fcSettings.find((x) => x.programSetting?.target === 0);
    const finalItem = fcSettings.find((x) => x.programSetting?.target === 1);

    const allDeviantClasses = [
      ...(moduleItem?.deviantClassSettings || []),
      ...(finalItem?.deviantClassSettings || []),
    ];
    const uniqueClassIds = [
      ...new Set(allDeviantClasses.map((c) => c.classId)),
    ];
    const deviantClasses = uniqueClassIds.map((classId) => {
      const cls = allDeviantClasses.find((c) => c.classId === classId);
      return { classId, className: cls?.className };
    });

    const allDeviantStudents = [
      ...(moduleItem?.deviantStudentSettings || []),
      ...(finalItem?.deviantStudentSettings || []),
    ];
    const uniqueStudentIds = [
      ...new Set(allDeviantStudents.map((s) => s.studentId)),
    ];
    const deviantStudents = uniqueStudentIds.map((studentId) => {
      const student = allDeviantStudents.find((s) => s.studentId === studentId);
      return {
        studentId,
        studentName: student?.studentName || null,
        className: student?.className || null,
      };
    });

    return { deviantClasses, deviantStudents };
  }, []);

  const onRefreshSettings = useCallback(async () => {
    const programId = String(props.programInfo?.data?.id);
    await queryClient.refetchQueries({ queryKey: ["FetchProgramById", programId] });
    const updatedProgram = queryClient.getQueryData([
      "FetchProgramById",
      programId,
    ]);
    return updatedProgram?.focusControlSettings || [];
  }, [queryClient, props.programInfo?.data?.id]);

  const fc = useFocusControl({
    settingKey: "programSetting",
    entityId: props.programInfo.data.id,
    entityIdKey: "programId",
    focusControlSettings: props.programInfo?.data?.focusControlSettings,
    saveFn: SaveProgramFocusControlSetting,
    onRefreshSettings,
    buildDeviantData,
  });

  const promptRemoveClass = (value) => {
    setClassForRemoval(value);
    setPromptClassRemoval(true);
  };

  const hideImportStudentsModal = () => {
    setImportStudentsModalOpen(false);
  };

  const deleteClassColumn = (value) => {
    return (
      <RequiresFullAccessHint hasFullAccess={props.teacherInfo.fullAccess}>
        <Button
          color="primary"
          className="p-1"
          outline
          disabled={!props.teacherInfo.fullAccess}
          onClick={() => promptRemoveClass(value)}
        >
          <FormattedMessage id="DeleteClass" />
        </Button>
      </RequiresFullAccessHint>
    );
  };

  const classesTableDataSource = [...props.programInfo.data.classes]
    .sort(compareByName)
    .map((cl) => ({ uniqueId: cl.id, name: cl.name, edit: deleteClassColumn(cl) }));

  const toggleAddClassModal = () => {
    setImportStudentsAfterCreatingClass(false);
    setAddClassModalOpen((prevState) => !prevState);
  };

  const closeAddClassModal = () => {
    setImportStudentsAfterCreatingClass(false);
    setAddClassModalOpen(false);
  };

  const toggleAddClassAndImportModal = () => {
    setImportStudentsAfterCreatingClass(true);
    setAddClassModalOpen((prevState) => !prevState);
  };

  const classesTableColumns = [
    {
      id: "name",
      key: "name",
      header: () => <FormattedMessage id="Name" />,
      width: 150,
      sortingFn: (a, b, columnId) =>
        compareStrings(a.getValue(columnId), b.getValue(columnId)),
    },
    {
      id: "edit",
      key: "edit",
      header: () => <FormattedMessage id="Edit" />,
      width: 150,
      sortable: false,
    },
  ];

  const reloadProgramAndTeacherData = () => {
    if (importStudentsModalOpen) {
      setImportStudentsModalOpen(false);
    }
    queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
    queryClient.invalidateQueries({ queryKey: ["FetchProgramById"] });
  };

  const handleDeleteClass = () => {
    deleteClassMutation.mutate(
      {
        id: classForRemoval.id,
      },
      {
        onSuccess: () => {
          setPromptClassRemoval(false);
          queryClient.invalidateQueries({ queryKey: ["FetchTeacherInformation"] });
          queryClient.invalidateQueries({ queryKey: ["FetchProgramById"] });
          toast.success(<FormattedMessage id="ClassDeleted" />);
        },
        onError: (err) => {
          setPromptClassRemoval(false);
          toast.warn(formatServiceError(intl, err?.response?.data));
        },
      }
    );
  };

  const handleClassCreated = (classId) => {
    setCreatedClassId(classId);
    if (importStudentsAfterCreatingClass) {
      setImportStudentsModalOpen(true);
    }
  };

  return (
    <div className="animated fadeIn">
      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="ProgramInformation" />
            <hr />
          </CardTitle>
          <Row>
            <Col sm="6">
              <Form>
                <Label htmlFor="programName" className="form-label">
                  <FormattedMessage id="Program" />:
                </Label>
                <span className="ms-2">{props.programInfo.data.name}</span>
              </Form>
            </Col>
          </Row>
        </Card>
      </Col>

      <FocusControl
        targetSettings={fc.targetSettings}
        deviantClasses={fc.deviantClasses}
        deviantStudents={fc.deviantStudents}
        deviationSource="Program"
        onSaveTarget={fc.handleSaveFocusTarget}
        savingTarget={fc.savingTarget}
      />

      <Col lg={12}>
        <Card body>
          <CardTitle>
            <FormattedMessage id="Classes" />
            <hr className="pb-0 mb-0" />
          </CardTitle>
          <CardSubtitle>
            <Row>
              <Col>
                <RequiresFullAccessHint
                  hasFullAccess={props.teacherInfo.fullAccess}
                >
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    className="my-1 m-y-md-0 me-md-1"
                    disabled={!props.teacherInfo.fullAccess}
                    onClick={toggleAddClassModal}
                  >
                    <FormattedMessage id="CreateNewClass" />
                  </Button>
                </RequiresFullAccessHint>
                <RequiresFullAccessHint
                  hasFullAccess={props.teacherInfo.fullAccess}
                >
                  <Button
                    size="sm"
                    color="primary"
                    outline
                    disabled={!props.teacherInfo.fullAccess}
                    className="my-1 m-y-md-0 mx-md-1"
                    onClick={toggleAddClassAndImportModal}
                  >
                    <FormattedMessage id="CreateAndImport" />
                  </Button>
                </RequiresFullAccessHint>
                <Button
                  size="sm"
                  color="primary"
                  outline
                  className="my-1 m-y-md-0 mx-md-1"
                  onClick={() =>
                    openChooseSidebarClasses(props.programInfo.data.id)
                  }
                >
                  <FormattedMessage id="ChooseClassesInSidebar" />
                </Button>
              </Col>
            </Row>
            <DataGrid
              columns={classesTableColumns}
              rows={classesTableDataSource}
            />
          </CardSubtitle>
        </Card>
      </Col>
      <ConfirmDialog
        isOpen={promptClassRemoval}
        toggle={() => setPromptClassRemoval(!promptClassRemoval)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          classForRemoval ? (
            <FormattedMessage
              values={{ classname: classForRemoval.name }}
              id="SureYouWantToDeleteClass"
            />
          ) : (
            ""
          )
        }
        onProceed={handleDeleteClass}
        danger={true}
      />
      <CreateClassInProgram
        isOpen={addClassModalOpen}
        toggle={closeAddClassModal}
        teachers={props.schoolInfo.data.teachers}
        program={props.programInfo.data}
        onClassCreated={handleClassCreated}
      />
      <ImportStudentsToClassModal
        preSelectedClass={true}
        preSelectedClassId={createdClassId}
        reloadClass={reloadProgramAndTeacherData}
        handleHide={hideImportStudentsModal}
        studentClasses={[]}
        isOpen={importStudentsModalOpen}
      />
    </div>
  );
}
